import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi, type ConfigPackage, type ConfigRelease } from "@/lib/forex-api";

type Toast = { type: "success" | "error"; msg: string };

const complianceKeys = ["app_terms", "app_compliance_copy"];

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "未发布";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 16);
}

function classifyConfig(configKey: string) {
  if (configKey === "app_terms") {
    return { label: "协议条款", icon: "ri-file-text-line", cls: "bg-brand/10 text-brand" };
  }
  return { label: "风险合规", icon: "ri-alert-line", cls: "bg-red-500/10 text-red-500" };
}

export default function ComplianceTab() {
  const [docs, setDocs] = useState<ConfigPackage[]>([]);
  const [releases, setReleases] = useState<ConfigRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState("");
  const [toast, setToast] = useState<Toast | null>(null);
  const [loadError, setLoadError] = useState(false);

  const loadDocs = () => {
    setLoading(true);
    setLoadError(false);
    Promise.all([forexApi.listConfigPackages(), forexApi.listConfigReleases()])
      .then(([packageResult, releaseResult]) => {
        setDocs(packageResult.items.filter((item) => complianceKeys.includes(item.config_key)));
        setReleases(releaseResult.items.filter((item) => complianceKeys.includes(item.config_key)));
      })
      .catch(() => { setLoadError(true); setToast({ type: "error", msg: "协议与合规加载失败，请确认后端服务或登录态。" }); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDocs();
  }, []);

  const handlePublish = async (configKey: string) => {
    const target = docs.find(item => item.config_key === configKey);
    if (!target || !window.confirm(`确认生成 ${target.label}（当前版本 ${target.version}）的后台发布记录？请先在详情核对内容，并独立核对 C 端。`)) return;
    setPublishing(configKey);
    try {
      const result = await forexApi.publishConfigPackage(configKey, {
        description: "协议与合规后台发布记录",
      });
      setDocs((prev) => prev.map((item) => (item.config_key === configKey ? result.package : item)));
      setReleases((prev) => [result.release, ...prev]);
      setToast({ type: "success", msg: "后台发布记录已生成；C 端实际读取结果待核对。" });
    } catch {
      setToast({ type: "error", msg: "发布结果未知，请先检查发布记录和 C 端配置，避免重复发布。" });
    } finally {
      setPublishing("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">协议与合规</h2>
          <p className="text-xs text-text-muted mt-0.5">
            管理后台协议与合规配置记录；发布记录不证明 GET /app/config/bootstrap 已生效。
          </p>
        </div>
        <span className="text-xs text-text-muted">{loading ? "加载中..." : loadError ? "配置包数量未知" : `共 ${docs.length} 个配置包`}</span>
      </div>

      {toast && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${toast.type === "success" ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600" : "border-red-500/20 bg-red-500/5 text-red-500"}`}>
          {toast.msg}
        </div>
      )}
      {!loading && loadError && <button onClick={loadDocs} className="text-sm text-brand underline">重新读取</button>}
      {!loading && !loadError && !docs.length && <p className="rounded-lg border bg-white p-8 text-center text-sm text-text-muted">暂无协议与合规配置包；不能据此判断 C 端已发布内容。</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {docs.map((doc) => {
          const meta = classifyConfig(doc.config_key);
          const releaseCount = releases.filter((item) => item.config_key === doc.config_key).length;
          return (
            <div key={doc.config_key} className="bg-white rounded-lg border border-surface-border p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${meta.cls}`}>
                    <i className={`${meta.icon} text-base`} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-text-primary">{doc.label}</h4>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] font-mono text-text-muted">{doc.version}</span>
                      <span className="text-[10px] text-text-muted">最后发布：{formatTime(doc.last_published_at)}</span>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${doc.status === "enabled" ? "bg-emerald-500/10 text-emerald-500" : "bg-text-muted/10 text-text-secondary"}`}>
                  <i className={`text-[8px] ${doc.status === "enabled" ? "ri-checkbox-circle-fill" : "ri-pause-circle-fill"}`} />
                  {doc.status === "enabled" ? "已启用" : "未启用"}
                </span>
              </div>

              <p className="text-xs text-text-secondary leading-relaxed mb-3">{doc.description}</p>
              <p className="mb-3 text-xs text-text-muted">发布前请查看详情核对正文与当前版本；此卡片只展示配置摘要。</p>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="rounded-md bg-surface/50 px-3 py-2">
                  <p className="text-[10px] text-text-muted">配置键</p>
                  <p className="text-[11px] text-text-primary font-mono mt-1">{doc.config_key}</p>
                </div>
                <div className="rounded-md bg-surface/50 px-3 py-2">
                  <p className="text-[10px] text-text-muted">发布记录</p>
                  <p className="text-[11px] text-text-primary font-mono mt-1">{releaseCount}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-surface-border">
                <Link to={`/forex/compliance/detail?type=${doc.config_key}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap">
                  <i className="ri-eye-line text-xs" />查看
                </Link>
                <button
                  onClick={() => handlePublish(doc.config_key)}
                  disabled={publishing === doc.config_key}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-brand/10 text-brand hover:bg-brand/20 whitespace-nowrap disabled:opacity-50"
                >
                  <i className={publishing === doc.config_key ? "ri-loader-4-line animate-spin text-xs" : "ri-upload-cloud-line text-xs"} />
                  {publishing === doc.config_key ? "发布中" : "生成发布记录"}
                </button>
                <Link to={`/forex/config/releases?type=${doc.config_key}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 whitespace-nowrap">
                  <i className="ri-file-list-3-line text-xs" />发布记录
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
